import { ViewportScroller, isPlatformBrowser } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  PLATFORM_ID,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { catchError, distinctUntilChanged, map, of, switchMap } from 'rxjs';

import {
  FrontendComparison,
  FrontendComparisonPoll,
  FrontendComparisonStore,
} from '../../../core/api/api.models';
import { AssetUrlPipe } from '../../../core/api/asset-url.pipe';
import { SavingArenaApiService } from '../../../core/api/saving-arena-api.service';
import { TrustedCmsHtmlPipe } from '../../../core/api/trusted-cms-html.pipe';
import { SeoService } from '../../../core/seo/seo.service';
import { SsrResponseService } from '../../../core/seo/ssr-response.service';
import { SectionHeading } from '../../../shared/section-heading/section-heading';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../core/seo/breadcrumb-trail';
import { faqPageSchema, itemListSchema, webPageSchema } from '../../../core/seo/structured-data';
import { Breadcrumb } from '../../../shared/breadcrumb/breadcrumb';

/** One row of the comparison table. */
interface MetricRow {
  key: string;
  label: string;
  /** Rendered value per side. '—' means "no data" — never render a missing metric as 0. */
  valueA: string;
  valueB: string;
}

@Component({
  selector: 'app-compare-page',
  imports: [Breadcrumb, RouterLink, AssetUrlPipe, TrustedCmsHtmlPipe, SectionHeading],
  templateUrl: './compare-page.html',
  styleUrl: './compare-page.scss',
})
export class ComparePage {
  private readonly api = inject(SavingArenaApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly seo = inject(SeoService);
  private readonly ssrResponse = inject(SsrResponseService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly viewportScroller = inject(ViewportScroller);
  private readonly host = inject(ElementRef<HTMLElement>);

  protected readonly comparison = signal<FrontendComparison | null>(null);

  /** "Comparisons", matching the hub — the visible crumb here used to read "Compare". */
  protected readonly crumbs = computed(() => {
    const title = this.comparison()?.title || '';
    return breadcrumbTrail(
      { label: 'Comparisons', path: '/compare' },
      ...(title ? [{ label: title }] : []),
    );
  });
  protected readonly isLoading = signal(true);
  protected readonly notFound = signal(false);

  protected readonly poll = signal<FrontendComparisonPoll | null>(null);
  protected readonly hasVoted = signal(false);
  protected readonly votedStoreId = signal('');
  protected readonly voteSubmitting = signal(false);
  protected readonly voteError = signal('');

  protected readonly expandedFaq = signal<number | null>(0);

  protected readonly storeA = computed(() => this.comparison()?.storeA ?? null);
  protected readonly storeB = computed(() => this.comparison()?.storeB ?? null);

  /**
   * The comparison table.
   *
   * Every value is formatted through helpers that render missing data as an em-dash rather than 0 —
   * "0% best discount" and "we don't know the best discount" are very different claims, and the API
   * deliberately sends `null` for the latter.
   */
  protected readonly metricRows = computed<MetricRow[]>(() => {
    const a = this.storeA();
    const b = this.storeB();
    const comparison = this.comparison();
    if (!a || !b || !comparison) return [];

    return [
      {
        key: 'activeCoupons',
        label: 'Active Coupons',
        valueA: this.num(a.activeCoupons),
        valueB: this.num(b.activeCoupons),
      },
      {
        key: 'verifiedCoupons',
        label: 'Verified Coupons',
        valueA: this.num(a.verifiedCoupons),
        valueB: this.num(b.verifiedCoupons),
      },
      {
        key: 'bestDiscount',
        label: 'Best Discount',
        valueA: this.percent(a.bestDiscount),
        valueB: this.percent(b.bestDiscount),
      },
      {
        key: 'hasCashback',
        label: 'Cashback Available',
        valueA: this.flag(a.hasCashback, a.cashbackText),
        valueB: this.flag(b.hasCashback, b.cashbackText),
      },
      {
        key: 'freeShipping',
        label: 'Free Shipping',
        valueA: this.flag(a.freeShipping, a.freeShippingText),
        valueB: this.flag(b.freeShipping, b.freeShippingText),
      },
      {
        key: 'studentDiscount',
        label: 'Student Discount',
        valueA: this.flag(a.studentDiscount, a.studentDiscountText),
        valueB: this.flag(b.studentDiscount, b.studentDiscountText),
      },
      {
        key: 'lastCouponUpdatedAt',
        label: 'Last Updated Coupon',
        valueA: this.date(a.lastCouponUpdatedAt),
        valueB: this.date(b.lastCouponUpdatedAt),
      },
      {
        key: 'rating',
        label: 'Store Rating',
        valueA: this.rating(a),
        valueB: this.rating(b),
      },
      {
        key: 'popularityScore',
        label: 'Popularity Score',
        valueA: this.num(a.popularityScore),
        valueB: this.num(b.popularityScore),
      },
    ];
  });

  protected readonly statBlocks = computed(() => {
    const a = this.storeA();
    const b = this.storeB();
    if (!a || !b) return [];

    const block = (store: FrontendComparisonStore) => ({
      store,
      rows: [
        { label: 'Store Founded', value: store.foundedYear ? String(store.foundedYear) : '—' },
        { label: 'Store Rating', value: this.rating(store) },
        { label: 'Active Coupons', value: this.num(store.activeCoupons) },
        { label: 'Categories', value: this.num(store.categories.length) },
        {
          label: 'Countries Served',
          value: store.countriesServed.length
            ? store.countriesServed.join(', ')
            : store.country || '—',
        },
        { label: 'Average Savings', value: this.percent(store.averageDiscount) },
      ],
    });

    return [block(a), block(b)];
  });

  protected isWinner(key: string, storeId: string): boolean {
    return this.comparison()?.winners?.[key] === storeId;
  }

  /**
   * The two hero cards. Each carries the three figures a reader actually decides on, plus how many
   * table rows that store wins — counted from the same `winners` map the table highlights, so the
   * hero can never claim a leader the table contradicts. On a tie neither side is flagged.
   */
  protected readonly heroSides = computed(() => {
    const comparison = this.comparison();
    const rows = this.metricRows();
    if (!comparison) return [];

    const winners = comparison.winners || {};
    const winsFor = (storeId: string) => rows.filter((row) => winners[row.key] === storeId).length;

    const aWins = winsFor(comparison.storeA.id);
    const bWins = winsFor(comparison.storeB.id);

    const side = (store: FrontendComparisonStore, wins: number, isLeader: boolean) => ({
      store,
      wins,
      total: rows.length,
      isLeader,
      stats: [
        { label: 'coupons', value: this.num(store.activeCoupons) },
        { label: 'best off', value: this.percent(store.bestDiscount) },
        { label: 'rating', value: store.rating ? store.rating.toFixed(1) : '—' },
      ],
    });

    return [
      side(comparison.storeA, aWins, aWins > bWins),
      side(comparison.storeB, bWins, bWins > aWins),
    ];
  });

  /**
   * Hero eyebrow. The comparison itself carries no category, so use the one the two stores share —
   * that is the only label true of the pair. Falls back to store A's leading category, and to ''
   * when neither has one, which hides the eyebrow.
   */
  protected readonly categoryLabel = computed(() => {
    const comparison = this.comparison();
    if (!comparison) return '';

    const a = comparison.storeA.categories ?? [];
    const b = comparison.storeB.categories ?? [];
    const shared = a.find((category) => b.some((other) => other.id === category.id));

    return (shared ?? a[0])?.name?.trim() ?? '';
  });

  /** In-page nav. Only sections actually rendered get a link, so none of them dead-ends. */
  protected readonly jumpLinks = computed(() => {
    const comparison = this.comparison();
    if (!comparison) return [];

    const links = [
      { id: 'cmp-table', label: 'Comparison' },
      { id: 'cmp-pros-cons', label: 'Pros & cons' },
      { id: 'cmp-stats', label: 'Statistics' },
      { id: 'cmp-poll', label: 'Poll' },
    ];

    if (comparison.faqs.length) links.push({ id: 'cmp-faqs', label: 'FAQs' });
    if (comparison.verdict) links.push({ id: 'cmp-verdict', label: 'Verdict' });

    return links;
  });

  /** The page H1. Prefers the admin's title, which is what the SEO copy is written around. */
  protected readonly heading = computed(() => {
    const comparison = this.comparison();
    if (!comparison) return '';
    return comparison.title || `${comparison.storeA.name} vs ${comparison.storeB.name}`;
  });

  protected readonly updatedLabel = computed(() => this.date(this.comparison()?.updatedAt));

  /** Spoken result of the poll. Empty until a vote lands, so nothing is announced on page load. */
  protected readonly pollAnnouncement = computed(() => {
    const comparison = this.comparison();
    const poll = this.poll();
    if (!comparison || !poll || !this.hasVoted()) return '';

    return (
      `Results: ${comparison.storeA.name} ${poll.storeAPercent} percent, ` +
      `${comparison.storeB.name} ${poll.storeBPercent} percent, from ${poll.totalVotes} ` +
      `${poll.totalVotes === 1 ? 'vote' : 'votes'}.`
    );
  });

  /**
   * "Zaful wins 5 of 9 rows · Auxbeam wins best discount" — counted from the same `winners` map the
   * table highlights, so the sentence can never disagree with the cells above it.
   */
  protected readonly winnerSummary = computed(() => {
    const comparison = this.comparison();
    const rows = this.metricRows();
    if (!comparison || !rows.length) return '';

    const winners = comparison.winners || {};
    const wonBy = (storeId: string) => rows.filter((row) => winners[row.key] === storeId);

    const a = wonBy(comparison.storeA.id);
    const b = wonBy(comparison.storeB.id);
    const decided = a.length + b.length;
    if (!decided) return 'No row has a clear winner — the two stores tie on every metric we track.';

    const [lead, chase] =
      a.length >= b.length
        ? [
            { store: comparison.storeA, rows: a },
            { store: comparison.storeB, rows: b },
          ]
        : [
            { store: comparison.storeB, rows: b },
            { store: comparison.storeA, rows: a },
          ];

    const parts = [`${lead.store.name} wins ${lead.rows.length} of ${rows.length} rows`];
    if (chase.rows.length) {
      const labels = chase.rows.map((row) => row.label.toLowerCase());
      const list =
        labels.length === 1
          ? labels[0]
          : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
      parts.push(`${chase.store.name} wins ${list}`);
    }

    return parts.join(' · ');
  });

  /**
   * The verdict band's score chips.
   *
   * Uses the real popularity score out of 100 rather than inventing an "8.8 / 10" rating — there is
   * no such field, and a made-up score on a page about real retailers would be a fabricated claim.
   */
  protected readonly verdictScores = computed(() => {
    const comparison = this.comparison();
    if (!comparison) return [];

    return [comparison.storeA, comparison.storeB]
      .filter((store) => Number.isFinite(store.popularityScore))
      .map((store) => ({ name: store.name, score: `${store.popularityScore} / 100` }));
  });

  constructor() {
    if (this.isBrowser) {
      // Anchor scrolling goes through `window.scrollTo`, which ignores `scroll-margin-top` — so a
      // jump link would land with the heading behind the sticky header. The offset is a callback so
      // it is measured per jump and stays right across breakpoints. Reset on destroy: the scroller
      // is app-wide and this offset is specific to this page's jump bar.
      this.viewportScroller.setOffset(() => [0, this.anchorOffset()]);
      this.destroyRef.onDestroy(() => this.viewportScroller.setOffset([0, 0]));
    }

    this.route.paramMap
      .pipe(
        map((params) => params.get('slug') || ''),
        // `paramMap` re-emits on any navigation to this route, including a fragment-only change
        // from the jump links. Without this the switchMap below would re-fetch and blank the page
        // (it resets `comparison` to null) every time the reader clicked one.
        distinctUntilChanged(),
        switchMap((slug) => {
          this.isLoading.set(true);
          this.notFound.set(false);
          this.comparison.set(null);
          this.poll.set(null);
          this.hasVoted.set(false);
          this.votedStoreId.set('');
          this.voteError.set('');

          return this.api.getComparison(slug).pipe(
            map((response) => ({ slug, response, error: null as unknown })),
            // A 404 here may carry `data.redirectTo` — the reverse pair wording. Keep the error body.
            catchError((error: unknown) => of({ slug, response: null, error })),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ slug, response, error }) => {
        this.isLoading.set(false);

        const comparison = response?.success ? response.data : null;

        if (!comparison) {
          this.handleMissingComparison(error);
          return;
        }

        this.comparison.set(comparison);
        this.poll.set(comparison.poll);
        this.applyComparisonSeo(comparison);
        this.loadVoteState(slug);
      });
  }

  /**
   * A comparison that didn't resolve is either a genuine 404, or the REVERSE wording of a real pair
   * ("shein-vs-zaful" for a page stored as "zaful-vs-shein"). Pairs have exactly one canonical URL,
   * so the reverse must 301 rather than 404 — otherwise every inbound link that guessed the order
   * would die (spec §6).
   */
  private handleMissingComparison(error: unknown): void {
    const body = (error as { error?: { data?: { redirectTo?: string } } })?.error;
    const redirectTo = body?.data?.redirectTo;

    if (redirectTo) {
      this.ssrResponse.redirectPermanent(redirectTo);
      this.seo.apply({ title: 'Redirecting…', noindex: true });
      this.seo.setJsonLd([]);
      return;
    }

    this.notFound.set(true);
    // A 5xx or a timeout is not "this comparison does not exist" — hand the cause over so
    // setNotFound can answer 503 instead of retiring a live URL.
    this.ssrResponse.noteFetchFailure(error);
    this.ssrResponse.setNotFound();
    this.seo.apply({
      title: 'Comparison not found | SavingArena',
      description: 'The store comparison you are looking for is not available.',
      noindex: true,
    });
    this.seo.setJsonLd([]);
  }

  private applyComparisonSeo(comparison: FrontendComparison): void {
    const a = comparison.storeA;
    const b = comparison.storeB;

    const title =
      comparison.metaTitle || `${a.name} vs ${b.name} Coupons & Deals Comparison | SavingArena`;
    const description =
      comparison.metaDescription ||
      `Compare ${a.name} and ${b.name} coupon codes, discounts, shipping offers and savings to find the best store for your next purchase.`;

    const path = `/compare/${comparison.slug}`;
    const url = this.seo.absolute(path);

    this.seo.apply({
      title,
      description,
      canonicalPath: path,
      // Quality gate: a comparison without real content is served noindex,follow so a pile of thin
      // pages can't drag the domain down (spec §17).
      noindex: !comparison.isIndexable,
    });

    this.seo.setJsonLd([
      webPageSchema({
        origin: this.seo.origin,
        url,
        name: title,
        description,
        dateModified: comparison.updatedAt || undefined,
      }),
      breadcrumbSchemaFor(this.crumbs(), (p) => this.seo.absolute(p)),
      itemListSchema(
        [a, b].map((store) => ({
          '@type': 'Organization',
          name: store.name,
          url: this.seo.absolute(store.path),
        })),
        comparison.title,
      ),
      // FAQPage only when the FAQs are actually rendered below.
      comparison.faqs.length ? faqPageSchema(comparison.faqs) : null,
    ]);
  }

  /** Browser-only — an IP-scoped answer must never be baked into cached SSR HTML. */
  private loadVoteState(slug: string): void {
    if (!this.isBrowser || !slug) return;

    this.api
      .getComparisonVoteState(slug)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        if (!response?.success) return;
        this.hasVoted.set(response.data.hasVoted);
        this.votedStoreId.set(response.data.votedStoreId);
      });
  }

  protected vote(store: FrontendComparisonStore): void {
    const comparison = this.comparison();
    if (!comparison || this.hasVoted() || this.voteSubmitting()) return;

    this.voteSubmitting.set(true);
    this.voteError.set('');

    this.api
      .submitComparisonVote(comparison.slug, store.slug)
      .pipe(
        catchError((error: unknown) => of({ error })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this.voteSubmitting.set(false);

        if ('error' in result) {
          // A 409 means this IP already voted — the body still carries the current tallies, so show
          // them rather than an error state.
          const body = (result.error as { error?: { data?: FrontendComparisonPoll } })?.error;
          if (body?.data) {
            this.poll.set(body.data);
            this.hasVoted.set(true);
            return;
          }
          this.voteError.set('Could not record your vote. Please try again.');
          return;
        }

        if (result.success) {
          this.poll.set(result.data);
          this.hasVoted.set(true);
          this.votedStoreId.set(store.id);
        }
      });
  }

  protected toggleFaq(index: number): void {
    this.expandedFaq.update((current) => (current === index ? null : index));
  }

  /**
   * How far a jump link must stop short of the target, in px: the site's sticky header plus this
   * page's own sticky jump bar, plus a little breathing room. The header figure is read from the
   * `--cmp-header-offset` custom property so the SCSS stays the single source of truth for it.
   */
  private anchorOffset(): number {
    const element = this.host.nativeElement as HTMLElement;
    const header =
      Number.parseInt(getComputedStyle(element).getPropertyValue('--cmp-header-offset'), 10) || 0;
    const jumpBar = element.querySelector('.sav-cmp-jump');
    const jumpHeight = jumpBar ? Math.round(jumpBar.getBoundingClientRect().height) : 0;

    return header + jumpHeight + 12;
  }

  protected initials(name: string): string {
    return (name || '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0]?.toUpperCase() ?? '')
      .join('');
  }

  // --- formatters: missing data renders as an em-dash, never as 0 ---------------------------------

  /** Protected, not private: the "Explore more" store links render a coupon count. */
  protected num(value: number | null | undefined): string {
    return value == null ? '—' : new Intl.NumberFormat('en-US').format(value);
  }

  private percent(value: number | null | undefined): string {
    return value == null ? '—' : `${value}%`;
  }

  private flag(value: boolean, text: string): string {
    if (text?.trim()) return text.trim();
    return value ? 'Yes' : 'No';
  }

  protected date(value: string | null | undefined): string {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '—';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(parsed);
  }

  private rating(store: FrontendComparisonStore): string {
    if (!store.ratingCount) return '—';
    return `${store.rating.toFixed(1)} (${store.ratingCount})`;
  }
}
